/**
 * CommercialApp — UI riêng cho commercial mode
 *
 * Tách biệt hoàn toàn khỏi App.tsx (internal).
 * Dùng lại core logic: translate workflow, file parser, Supabase, stores.
 * UI/layout/routing riêng.
 *
 * Entry: index.tsx chọn App.tsx hoặc CommercialApp.tsx dựa trên VITE_APP_MODE
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useUtmCapture } from './hooks/useUtmCapture';
import { useProjectStore } from './store/useProjectStore';
import { useConfigStore } from './store/useConfigStore';
import { useDebugStore } from './store/useDebugStore';
import { useAutoSave } from './hooks/useAutoSave';
import { useDispatchStore } from './store/useDispatchStore';
import { useBatchRecovery } from './hooks/useBatchRecovery';
import { useFileSystem } from './hooks/useFileSystem';
import { useFileSystemStore } from './store/useFileSystemStore';
import { saveFileImmediately } from './services/shared/saveFileImmediately';
import { useTranslateWorkflow } from './features/translate/hooks/useTranslateWorkflow';
import { useTrainWorkflow, getTrainSourceSegments } from './features/train/hooks/useTrainWorkflow';
import { getLineSegments as getSegments } from './utils/textUtils';
import { simpleHash } from './utils/hashUtils';
import { useProjectActions } from './hooks/useProjectActions';
import { useFileActions } from './hooks/useFileActions';
import { useJobManager } from './contexts/JobManagerContext';
import { PromptConfig, Project, ProjectType, TranslationFile, AppView, DebugLog } from './types';
import { useHashRouter } from './hooks/useHashRouter';
import { useLocation, useNavigate } from 'react-router-dom';

// Layout
import Header from './components/layout/Header';
import Sidebar, { SidebarMode } from './components/layout/Sidebar';
import AdminDashboard from './components/admin/AdminDashboard';

// Shared components (reuse from internal)
import CreateProjectModal from './components/modals/CreateProjectModal';
import ProjectsList from './components/ProjectsList';
import ProjectDetail from './components/ProjectDetail';
import TerminologyGlobal from './components/TerminologyGlobal';

// Commercial-only components
import LandingPage from './components/commercial/LandingPage';
import CreditShop from './components/commercial/CreditShop';
import CreditBadge from './components/commercial/CreditBadge';
import NotificationBell from './components/commercial/NotificationBell';
import PrivacyBadge from './components/commercial/PrivacyBadge';
import PrivacyPolicyPage from './components/commercial/PrivacyPolicyPage';
import PhoneVerify from './components/commercial/PhoneVerify';
import JobsDrawer from './components/commercial/JobsDrawer';
import RunAllModal from './components/commercial/RunAllModal';
import BankTransferModal from './components/commercial/BankTransferModal';
import CreditConfirmModal from './components/commercial/CreditConfirmModal';
import BugReportModal from './components/commercial/BugReportModal';
import OnboardingTour from './components/commercial/OnboardingTour/OnboardingTour';
import Stage4ProjectTour from './components/commercial/OnboardingTour/Stage4ProjectTour';
import { useCredits } from './hooks/useCredits';
import { useAccurateTokenCount } from './hooks/useAccurateTokenCount';
import { CelebrationModal, DataShredder } from './components/shared/CelebrationModal';
import { useToast } from './components/shared/Toast';

// Features - Translate UI (reuse)
import TranslationExportWrapper from './features/translate/ui/TranslationExportWrapper';

// Features - Train UI (reuse)
import SourceInput from './features/train/ui/SourceInput';
import BenchmarkInput from './features/train/ui/BenchmarkInput';
import TranslationReview from './features/train/ui/TranslationReview';
import SemanticMatch from './features/train/ui/SemanticMatch';
import AuditReport from './features/train/ui/AuditReport';

/**
 * Paragraph-level segments for UI display (translate tab).
 * Merges layer-2 run-groups back into paragraph text with positions
 * in sourceText (= backendSegments.join('\n')).
 * Falls back to getLineSegments for files without backendParagraphs.
 */
const getDisplaySegments = (file: TranslationFile): { text: string; start: number; end: number }[] => {
  if (!file.backendParagraphs?.length || !file.backendSegments?.length) {
    return getSegments(file.sourceText);
  }
  const bSegs = file.backendSegments;
  const positions: { start: number; end: number }[] = [];
  let pos = 0;
  for (const s of bSegs) {
    positions.push({ start: pos, end: pos + s.length });
    pos += s.length + 1;
  }
  const result: { text: string; start: number; end: number }[] = [];
  for (const p of file.backendParagraphs) {
    const aiIdxs = p.groups.filter((g: any) => g.aiIdx !== undefined).map((g: any) => g.aiIdx as number);
    if (aiIdxs.length === 0) continue;
    const firstIdx = Math.min(...aiIdxs);
    const lastIdx = Math.max(...aiIdxs);
    const text = p.groups.map((g: any) =>
      g.aiIdx !== undefined ? (bSegs[g.aiIdx] ?? '') : (g.wsText ?? '')
    ).join('');
    if (!text.trim()) continue;
    result.push({ text, start: positions[firstIdx].start, end: positions[lastIdx].end });
  }
  return result;
};

const CommercialApp: React.FC = () => {
  useUtmCapture(); // First-touch UTM → localStorage; persisted on signup via AuthContext

  const { user, profile, isAdmin, session, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ── Navigation ──
  const [currentView, setCurrentView] = useState<AppView>('projects');
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('translating');

  // URL → sidebarMode sync (US-07-09 React Router migration)
  // /app            → translating
  // /app/training   → training
  // /app/terminology→ terminology
  // /app/shop       → shop
  // /admin          → admin
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin')) setSidebarMode('admin');
    else if (path === '/app/shop' || path === '/app/shop/') setSidebarMode('shop' as SidebarMode);
    else if (path === '/app/training' || path === '/app/training/') setSidebarMode('training');
    else if (path === '/app/terminology' || path === '/app/terminology/') setSidebarMode('terminology');
    else setSidebarMode('translating'); // /app default
    setCurrentView('projects');
  }, [location.pathname]);

  // ── Stores (reuse) ──
  const {
    projects, files, selectedProjectId, selectedFileId,
    addProject, updateProject, deleteProject,
    addFile, updateFile, deleteFile,
    selectProject, selectFile
  } = useProjectStore();

  const { config, setConfig } = useConfigStore();
  const { debugLogs, showDebug, setShowDebug, addDebugLog } = useDebugStore();

  // ── Hooks (reuse) ──
  useAutoSave();
  useBatchRecovery();

  // beforeunload prompt khi đang dispatch API insertJobs (translate/train).
  // Bảo vệ user khỏi refresh giữa chừng làm mất state.
  const dispatchInFlight = useDispatchStore((s) => s.inFlightCount > 0);
  useEffect(() => {
    if (!dispatchInFlight) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Một số browser (Firefox older, Safari) cần returnValue non-empty mới hiện prompt.
      // Modern Chrome ignore message text — vẫn show generic warning.
      e.returnValue = 'Đang gửi job lên server, refresh có thể mất tiến độ.';
      return 'Đang gửi job lên server, refresh có thể mất tiến độ.';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dispatchInFlight]);

  // US-19-16: subscribe isSyncingFromDisk for skeleton UI during background hydrate
  const isSyncingFromDisk = useFileSystemStore((s) => s.isSyncingFromDisk);
  // US-12-07 follow-up: isInitializing covers initial handle lookup + permission query window.
  // Without this, refresh briefly flashes "kết nối thư mục" prompt before connect/sync state set.
  const isInitializing = useFileSystemStore((s) => s.isInitializing);

  const {
    isConnected: isFSConnected,
    folderName: fsFolderName,
    lastError: fsError,
    needsPermission: fsNeedsPermission,
    connect: handleConnectFS,
    disconnect: handleDisconnectFS,
    initialize: initFS,
    reGrantPermission: handleReGrantFS
  } = useFileSystem();

  useEffect(() => { initFS(); }, [initFS]);

  // (US-19-16 file-detail content-ensure useEffect moved below currentFile declaration)

  // ── DEV: bypass folder gate cho Playwright/headless test (US-20-01) ──
  // Kích hoạt: `localStorage.setItem('dev_skip_folder', '1')` + reload (dev mode only).
  // Downstream auto-save/saveOutput skip tự động vì check `rootHandle` (null khi bypass).
  const devSkipFolder = import.meta.env.DEV
    && typeof window !== 'undefined'
    && window.localStorage.getItem('dev_skip_folder') === '1';
  const isFSConnectedEffective = isFSConnected || devSkipFolder;
  const fsFolderNameEffective = fsFolderName || (devSkipFolder ? '[DEV] In-Memory (bypass)' : '');

  const { addJob, updateJob, completeJob, failJob } = useJobManager();

  const {
    handleCreateProject,
    handleUpdateProject,
    handleImportProject,
    handleExportProject,
    handleOpenFolder,
    handleDeleteProject,
    handleRenameProject
  } = useProjectActions();

  const {
    handleAddFiles,
    handleBatchImport,
    handleDeleteFile,
    handleUpdateFile,
  } = useFileActions();

  // ── Translate workflow (reuse) ──
  const {
    runTranslateJob,
    runQualityCheck,
    retryBatches,
    isTranslating,
    realtimeProgress,
    realtimeBatches,
    activeTranslatingFileId,
    activeTotalSegments,
    bugReportContext,
    clearBugReportContext,
  } = useTranslateWorkflow();

  // ── Train workflow (reuse) ──
  const {
    runFileAuto,
    runAuditStep,
    isMatching,
    isAuditing,
    auditStreamingText,
  } = useTrainWorkflow();

  // Local wrappers to match App.tsx naming used in JSX below
  // US-19-16: ensure file content loaded before audit workflow reads heavy fields
  const runAudit = async (fileId: string) => {
    const { ensureFileContentLoaded } = await import('./hooks/useFileSystem');
    await ensureFileContentLoaded(fileId);
    return runAuditStep(fileId);
  };

  const runSemanticMatchStep = async (fileId: string) => {
    // US-19-16: ensure content loaded before semantic-match reads sourceText + humanBenchmark
    const { ensureFileContentLoaded } = await import('./hooks/useFileSystem');
    await ensureFileContentLoaded(fileId);

    const freshFiles = useProjectStore.getState().files;
    const freshProjects = useProjectStore.getState().projects;
    const file = freshFiles.find(f => f.id === fileId);
    const project = freshProjects.find(p => p.id === file?.projectId);
    if (!file || !project) return;
    const humanBenchmark = file.humanBenchmark || project.humanBenchmark || '';
    if (!humanBenchmark) return;
    const sourceSegments = getSegments(file.sourceText).map((s: { text: string }) => s.text);
    const humanSegments = getSegments(humanBenchmark).map((s: { text: string }) => s.text);
    const jobId = addJob('semantic-match', fileId, file.fileName);
    handleUpdateFile(fileId, { matchStatus: 'running', semanticMatchResult: undefined, error: undefined });
    try {
      const { runJobAndWait } = await import('./services/api/workerClient');
      const { useConfigStore: _cs } = await import('./store/useConfigStore');
      const model = _cs.getState().config.model;
      const { result, usage } = await runJobAndWait({
        type: 'semantic-match',
        fileId,
        fileName: file.fileName,
        input: { viSegments: sourceSegments, enSegments: humanSegments, model },
      });
      const fullUsage = { ...usage, totalTokens: usage.inputTokens + usage.outputTokens };
      completeJob(jobId, fullUsage);
      handleUpdateFile(fileId, {
        semanticMatchResult: {
          mappings: result.mappings,
          alignedHumanSegments: result.alignedHumanSegments,
          rawOutput: '',
          unmatchedCount: result.unmatchedCount,
          usage: fullUsage,
        },
        matchStatus: 'done',
        matchSourceHash: simpleHash(file.sourceText),
        matchBenchmarkHash: simpleHash(humanBenchmark),
      });
    } catch (e: any) {
      failJob(jobId, e.message);
      handleUpdateFile(fileId, { matchStatus: 'error', error: e.message });
    }
  };

  // ── Quality Check handler (US-11-04 + US-11-07: language selector) ──
  const [isQualityChecking, setIsQualityChecking] = useState(false);
  const [qcOutputLang, setQcOutputLang] = useState<string | null>(null); // null = use project.sourceLang default

  // QC trigger → credit-gated (US-04-10)
  const handleRunQualityCheck = () => {
    if (!currentFile || !currentProject || currentProject.type !== 'translate') return;
    handleRunFileWithCreditCheck(currentFile.id, 'qc');
  };

  // ── Commercial state ──
  const [createModalMode, setCreateModalMode] = useState<ProjectType | 'select' | null>(null);
  const [showJobs, setShowJobs] = useState(false);
  const [projectDetailTab, setProjectDetailTab] = useState<'translate' | 'train' | 'glossary'>('translate');
  const [returnToFileId, setReturnToFileId] = useState<string | null>(null);

  // ── Run All modal (multi-file selector) ──
  const [showRunAll, setShowRunAll] = useState(false);

  // ── Hoạt động drawer (jobs cross-project view) ──
  const [jobsOpen, setJobsOpen] = useState(false);
  const jobsMode: 'translate' | 'train' = sidebarMode === 'training' ? 'train' : 'translate';
  const jobsRunningCount = files.filter(f =>
    f.translationStatus === 'running' ||
    f.matchStatus === 'running' ||
    f.auditStatus === 'running'
  ).length;
  const jobsButtonVisible = sidebarMode === 'translating' || sidebarMode === 'training';

  // ── OTP / Credits / Payment ──
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const [otpDismissed, setOtpDismissed] = useState(() => sessionStorage.getItem('otp_dismissed') === 'true');
  const { balance, phoneVerified, verifyPhone, checkCredits, refetch: refetchCredits } = useCredits();
  const { countBundle } = useAccurateTokenCount();

  // Background preflight: file vừa import nhưng chưa có estimatedCredits (user click Tiến hành Nạp
  // trước khi token đếm xong) → tự fire countBundle, update file khi xong.
  const pendingPreflightRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const file of files) {
      if (file.estimatedCredits) continue;
      if (!file.sourceText) continue;
      if (pendingPreflightRef.current.has(file.id)) continue;
      const project = projects.find(p => p.id === file.projectId);
      if (!project) continue;

      pendingPreflightRef.current.add(file.id);
      (async () => {
        try {
          const sourceSegments = file.sourceText.split('\n').filter(s => s.trim().length > 0);
          const humanSegments = file.humanBenchmark
            ? file.humanBenchmark.split('\n').filter(s => s.trim().length > 0)
            : undefined;
          const bundleType: 'translate' | 'train' = project.type === 'train' ? 'train' : 'translate';
          const bundle = await countBundle({
            bundle_type: bundleType,
            segments: sourceSegments,
            humanSegments,
            sourceLang: project.sourceLang || 'vi',
            targetLang: project.targetLang || 'en',
          });
          if (bundle) {
            // estimatedCredits is single source of truth — UI badges resolve via selectPrimaryCredits selector
            updateFile(file.id, {
              estimatedCredits: {
                translate: bundle.breakdown.translate,
                qc: bundle.breakdown.qc ?? undefined,
                semanticMatch: bundle.breakdown.semanticMatch ?? undefined,
                audit: bundle.breakdown.audit ?? undefined,
                pricingVersion: bundle.pricingVersion ?? undefined,
                bundleType: bundle.bundleType,
                computedAt: bundle.computedAt,
              },
            });
          }
        } catch (e) {
          console.warn('[BackgroundPreflight] failed for', file.fileName, e);
        } finally {
          pendingPreflightRef.current.delete(file.id);
        }
      })();
    }
  }, [files, projects, countBundle, updateFile, config.model]);

  // Deferred OTP: hiện popup 1 lần duy nhất sau login, không lặp lại nếu đã bỏ qua
  // TẠM ẨN — chưa wire chương trình +100K (cùng lý do với Gift banner trong Sidebar).
  // Re-enable: đổi `false &&` thành điều kiện gốc.
  useEffect(() => {
    if (false && user && !phoneVerified && !otpDismissed) {
      const timer = setTimeout(() => setShowPhoneVerify(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user, phoneVerified, otpDismissed]);

  // ── Toast ──
  const { showToast } = useToast();

  // ── Celebration + Shredder ──
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationData, setCelebrationData] = useState({ title: '', subtitle: '', value: '' });
  const [showShredder, setShowShredder] = useState(false);

  // ── Bank transfer payment state ──
  const [showBankTransfer, setShowBankTransfer] = useState(false);
  const [purchaseData, setPurchaseData] = useState<{ id: string; transfer_code: string; credits_total: number; price_vnd: number } | null>(null);

  // ── Credit confirm state ──
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [pendingTranslateFileId, setPendingTranslateFileId] = useState<string | null>(null);
  const [pendingRunMode, setPendingRunMode] = useState<'translate' | 'train' | 'qc' | 'audit' | 'retranslate'>('translate');
  const [creditCheckResult, setCreditCheckResult] = useState<any>(null);

  // ── Derived ──
  const currentProject = projects.find(p => p.id === selectedProjectId);
  const currentFile = files.find(f => f.id === selectedFileId);
  const streamingCountForQueue = realtimeProgress?.totalTranslated || 0;

  // US-19-16 Stage 1 round 2 concern #3 + Stage 2 caveat: ensure file content lazy-load
  // when file-detail view restored WITHOUT going through handleSelectFile (URL refresh, deep link).
  // Deps include selectedFileId + currentFile?.contentLoaded + files.length so effect re-runs
  // after metadata files hydrate (avoid stuck-spinner if view set before files loaded).
  useEffect(() => {
    if (currentView !== 'file-detail') return;
    if (!selectedFileId) return;
    if (!currentFile || currentFile.contentLoaded !== false) return;
    import('./hooks/useFileSystem').then(({ ensureFileContentLoaded }) => {
      ensureFileContentLoaded(selectedFileId).catch(e => console.warn('[CommercialApp] effect ensure failed:', e));
    });
  }, [currentView, selectedFileId, currentFile?.contentLoaded, files.length]);

  // ── Hover segment state (for train SourceInput) ──
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null);

  // ── Breadcrumb ──
  const { buildBreadcrumbs } = useHashRouter();
  const breadcrumbItems = buildBreadcrumbs(currentProject?.name, currentFile?.fileName);
  const handleBreadcrumbNavigate = (hash: string) => { window.location.hash = hash; };

  // ── Credit-gated translate/train (US-04-10: server accurate count, bỏ char-based) ──

  const handleRunFileWithCreditCheck = async (
    fileId: string,
    mode: 'translate' | 'train' | 'qc' | 'audit' | 'retranslate' = 'translate',
  ) => {
    // US-19-16: ensure file content loaded BEFORE reading sourceText for credit count
    const { ensureFileContentLoaded } = await import('./hooks/useFileSystem');
    await ensureFileContentLoaded(fileId);

    const freshFiles = useProjectStore.getState().files;
    const freshProjects = useProjectStore.getState().projects;
    const file = freshFiles.find(f => f.id === fileId);
    const project = freshProjects.find(p => p.id === file?.projectId);
    if (!file?.sourceText) {
      showToast('Tài liệu chưa có nội dung. Vui lòng tải lên file trước.', 'error');
      return;
    }

    // QC + Audit cần aiOutput — nếu chưa có thì không pre-flight được
    if ((mode === 'qc' || mode === 'audit') && (!file.aiOutput || file.aiOutput.length === 0)) {
      showToast('Cần dịch tài liệu trước khi chạy QC / Audit.', 'error');
      return;
    }

    // Show credit modal — `isComputing` chỉ true khi thực sự cần countBundle (file thiếu estimatedCredits)
    const ecCached = file.estimatedCredits;
    setCreditCheckResult({ isComputing: !ecCached, fileName: file.fileName });
    setPendingTranslateFileId(fileId);
    setPendingRunMode(mode);
    setShowCreditConfirm(true);

    try {
      const sourceSegments = getSegments(file.sourceText).map(s => s.text);
      const sourceLang = project?.sourceLang || 'vi';
      const targetLang = project?.targetLang || 'en';
      const aiSegments = file.aiOutput || [];

      let totalCredits = 0;
      const breakdown: Array<{ agent: string; credits: number; model?: string; inputTokens?: number }> = [];

      let ec = ecCached;
      // Auto-run preflight if missing (fix single-file drag-drop bug — handleAddFiles không pass estimatedCredits)
      if (!ec) {
        const bundleType: 'translate' | 'train' = mode === 'train' ? 'train' : 'translate';
        const humanSegments = file.humanBenchmark
          ? file.humanBenchmark.split('\n').filter(s => s.trim().length > 0)
          : undefined;
        const bundle = await countBundle({
          bundle_type: bundleType,
          segments: sourceSegments,
          humanSegments,
          sourceLang,
          targetLang,
        });
        if (!bundle) {
          setShowCreditConfirm(false);
          setPendingTranslateFileId(null);
          showToast('Không tính được credit pre-flight. Vui lòng thử lại.', 'error');
          return;
        }
        ec = {
          translate: bundle.breakdown.translate,
          qc: bundle.breakdown.qc ?? undefined,
          semanticMatch: bundle.breakdown.semanticMatch ?? undefined,
          audit: bundle.breakdown.audit ?? undefined,
          pricingVersion: bundle.pricingVersion ?? undefined,
          bundleType: bundle.bundleType,
          computedAt: bundle.computedAt,
        };
        updateFile(fileId, { estimatedCredits: ec });
        // Force-save xuống disk ngay — tránh race condition: user reload trước khi auto-save kịp → mất cache → đếm lại.
        const fsState = useFileSystemStore.getState();
        if (fsState.isConnected && fsState.rootHandle && project) {
          const freshFile = useProjectStore.getState().files.find(f => f.id === fileId);
          if (freshFile) {
            saveFileImmediately(fsState.rootHandle, project, freshFile).catch(err =>
              console.warn('[Preflight] Failed to persist estimatedCredits:', err)
            );
          }
        }
      }

      if (mode === 'train') {
        // Train chain = translate + match (free) + audit
        if (ec.bundleType !== 'train' || ec.audit == null) {
          setShowCreditConfirm(false);
          setPendingTranslateFileId(null);
          showToast('Cần mẫu đối chiếu để chạy Huấn luyện. Vui lòng tải lại file với benchmark.', 'error');
          return;
        }
        totalCredits = ec.translate + (ec.semanticMatch ?? 0) + (ec.audit ?? 0);
        breakdown.push({ agent: 'Dịch', credits: ec.translate });
        breakdown.push({ agent: 'Ghép cặp', credits: ec.semanticMatch ?? 0, model: 'gemini-3.1-flash-lite' });
        breakdown.push({ agent: 'Audit', credits: ec.audit });
      } else if (mode === 'qc') {
        if (ec.qc == null) {
          setShowCreditConfirm(false);
          setPendingTranslateFileId(null);
          showToast('File này chưa có credit QC pre-flight. Vui lòng tải lại file.', 'error');
          return;
        }
        totalCredits = ec.qc;
        breakdown.push({ agent: 'Kiểm tra chất lượng (QC)', credits: ec.qc });
      } else if (mode === 'audit') {
        if (ec.audit == null) {
          setShowCreditConfirm(false);
          setPendingTranslateFileId(null);
          showToast('File này chưa có credit Audit pre-flight. Vui lòng tải lại file với benchmark.', 'error');
          return;
        }
        totalCredits = ec.audit;
        breakdown.push({ agent: 'Audit (AI vs Human)', credits: ec.audit });
      } else {
        // translate / retranslate
        totalCredits = ec.translate;
        breakdown.push({
          agent: mode === 'retranslate' ? 'Dịch lại từ đầu' : 'Dịch',
          credits: ec.translate,
        });
      }

      const check = checkCredits(totalCredits);
      setCreditCheckResult({
        ...check,
        fileName: file.fileName,
        breakdown,
        totalCredits,
      });
    } catch (err: any) {
      console.error('[Commercial] Accurate count failed:', err);
      setShowCreditConfirm(false);
      setPendingTranslateFileId(null);
      showToast('Không tính được credit chính xác, vui lòng thử lại.', 'error');
    }
  };

  const handleConfirmTranslate = async () => {
    setShowCreditConfirm(false);
    if (!pendingTranslateFileId) return;

    const fileId = pendingTranslateFileId;
    const mode = pendingRunMode;
    setPendingTranslateFileId(null);

    // US-19-16: ensure file content loaded before train/qc workflows read heavy fields.
    // (translate/audit already ensure inside their hooks; train calls runFileAuto which doesn't.)
    const { ensureFileContentLoaded } = await import('./hooks/useFileSystem');
    await ensureFileContentLoaded(fileId);

    try {
      if (mode === 'train') {
        await runFileAuto(fileId);
        showToast('Phân tích hoàn tất!', 'success');
      } else if (mode === 'qc') {
        const freshFiles = useProjectStore.getState().files;
        const freshProjects = useProjectStore.getState().projects;
        const f = freshFiles.find(x => x.id === fileId);
        const p = freshProjects.find(x => x.id === f?.projectId);
        if (!f || !p) return;
        setIsQualityChecking(true);
        try {
          const { getLineSegments } = await import('./utils/textUtils');
          const { synonymGroupsToFlatText } = await import('./utils/glossaryUtils');
          const sourceSegments = getLineSegments(f.sourceText).map(s => s.text);
          const aiOutput = f.aiOutput || [];
          const projectConfig = {
            ...config,
            systemInstruction: p.systemPrompt || config.systemInstruction,
            extraRules: synonymGroupsToFlatText(p.importedTerminology || []) || config.extraRules,
            synonymGroups: p.importedTerminology || [],
            locale: p.locale || config.locale,
            sourceLang: p.sourceLang || 'vi',
            targetLang: p.targetLang || 'en',
            qcOutputLang: qcOutputLang || 'vi',
          };
          await runQualityCheck(fileId, sourceSegments, aiOutput, projectConfig);
          showToast('Kiểm tra chất lượng hoàn tất!', 'success');
        } finally {
          setIsQualityChecking(false);
        }
      } else if (mode === 'audit') {
        await runAudit(fileId);
        showToast('Audit hoàn tất!', 'success');
      } else {
        // 'translate' hoặc 'retranslate' — cùng action
        await runTranslateJob(fileId);
        showToast(mode === 'retranslate' ? 'Dịch lại hoàn tất!' : 'Dịch hoàn tất!', 'success');
      }
    } catch (err: any) {
      console.error('[Commercial] Run error:', err);
      showToast(err?.message || 'Hệ thống đang bận, vui lòng thử lại. Bạn không bị trừ credits.', 'error');
    }
  };

  // ── Navigation helpers ──
  const handleSelectProject = (id: string) => {
    selectProject(id);
    setCurrentView('project-detail');
  };

  const handleSelectFile = async (fileId: string) => {
    selectFile(fileId);
    setCurrentView('file-detail');
    // US-19-16: lazy-load file content (sourceText, originalFile, etc) when user opens detail.
    // Background fetch — UI shows file shell immediately, content fields populate when loaded.
    const { ensureFileContentLoaded } = await import('./hooks/useFileSystem');
    ensureFileContentLoaded(fileId).catch(e => console.warn('[CommercialApp] ensure content failed:', e));
  };

  const handleBackToProjects = () => {
    setCurrentView('projects');
  };

  const handleBackToProject = () => {
    setCurrentView('project-detail');
  };

  // ── Sidebar mode change → navigate URL, useEffect above syncs state ──
  const handleModeChange = (mode: SidebarMode) => {
    const pathMap: Record<string, string> = {
      translating: '/app',
      training: '/app/training',
      terminology: '/app/terminology',
      shop: '/app/shop',
      admin: '/admin',
    };
    navigate(pathMap[mode] || '/app');
  };

  // ── Render ──
  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900">

      {/* Sidebar */}
      <Sidebar
        activeMode={sidebarMode}
        onModeChange={handleModeChange}
        counts={{
          translating: projects.filter(p => p.type === 'translate').length,
          training: projects.filter(p => p.type !== 'translate').length,
        }}
        onCreateProject={() => setCreateModalMode('select')}
        isAdmin={isAdmin}
        userName={user?.email?.split('@')[0] || 'User'}
        userEmail={user?.email || ''}
        userRole={isAdmin ? 'admin' : 'user'}
        credits={balance?.credits ?? 0}
        onSignOut={signOut}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Header */}
        <Header
          onReset={handleBackToProjects}
          onSelectFile={handleSelectFile}
          fsStatus={{
            isConnected: isFSConnectedEffective,
            folderName: fsFolderNameEffective,
            error: fsError || undefined,
          }}
          onConnectFS={handleConnectFS}
          breadcrumbItems={breadcrumbItems}
          onBreadcrumbNavigate={handleBreadcrumbNavigate}
          onToggleJobsDrawer={jobsButtonVisible ? () => setJobsOpen((o: boolean) => !o) : undefined}
          jobsRunningCount={jobsRunningCount}
        />

        {/* Hoạt động drawer (cross-page) */}
        <JobsDrawer
          isOpen={jobsOpen}
          onClose={() => setJobsOpen(false)}
          mode={jobsMode}
          files={files}
          projects={projects}
        />

        {/* Notification Bell — floating top-right */}
        {user && (
          <div className="fixed top-3 right-3 z-40">
            <NotificationBell accessToken={session?.access_token} />
          </div>
        )}

        {/* Content */}
        <main className="flex-1 overflow-hidden relative">

          {/* Credit Shop */}
          {sidebarMode === 'shop' as any && (
            <CreditShop
              onBack={() => navigate('/app')}
              onSelectPack={async (pack) => {
                try {
                  const token = session?.access_token;
                  if (!token) { showToast('Vui lòng đăng nhập lại', 'error'); return; }
                  const res = await fetch(`${import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787'}/api/credits/purchase`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ packId: pack.id, paymentMethod: 'bank' }),
                  });
                  if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Không thể tạo đơn', 'error'); return; }
                  const data = await res.json();
                  setPurchaseData(data.purchase);
                  setShowBankTransfer(true);
                } catch (err: any) {
                  showToast(err.message || 'Lỗi tạo đơn hàng', 'error');
                }
              }}
            />
          )}

          {/* Admin Dashboard — gated by isAdmin role (commercial + internal share) */}
          {sidebarMode === 'admin' && isAdmin && (
            <AdminDashboard />
          )}

          {/* Privacy Policy */}
          {currentView === 'privacy' as any && (
            <PrivacyPolicyPage onBack={() => setCurrentView('projects')} />
          )}

          {/* US-12-07 follow-up: polished skeleton matching real ProjectsList layout.
              Covers 2 phases: (1) isInitializing — handle lookup + permission query;
              (2) isSyncingFromDisk + projects empty — disk scan in progress.
              Card structure mirrors ProjectsList rows so transition is smooth. */}
          {currentView === 'projects' && sidebarMode !== 'terminology' && sidebarMode !== ('shop' as any) && sidebarMode !== 'admin'
            && (isInitializing || (isSyncingFromDisk && projects.length === 0)) && (
              <div className="max-w-5xl mx-auto px-4 py-6">
                {/* Header section skeleton */}
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="h-7 w-32 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded animate-pulse" />
                    <div className="h-5 w-12 rounded-full bg-slate-100 animate-pulse" />
                  </div>
                  <div className="h-9 w-28 rounded-lg bg-gradient-to-r from-indigo-100 to-indigo-50 animate-pulse" />
                </div>

                {/* Status pill — soft loading message */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 bg-indigo-50 border border-indigo-100 rounded-full">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2.5" className="text-indigo-500" style={{ animation: 'spin 1.2s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  <span className="text-[11px] font-semibold text-indigo-700 tracking-wide">
                    {isInitializing ? 'Đang khởi động workspace...' : 'Đang đồng bộ dự án từ ổ đĩa...'}
                  </span>
                </div>

                {/* Card with row items mirroring ProjectsList */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  {[1, 2, 3].map((i) => (
                    <div key={i}
                         className={`flex items-center gap-3 px-4 py-3.5 ${i < 3 ? 'border-b border-slate-100' : ''}`}
                         style={{ animationDelay: `${i * 100}ms` }}>
                      {/* Icon circle (matches w-9 h-9 indigo/amber rounded-lg) */}
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 shrink-0 animate-pulse"
                           style={{ animationDelay: `${i * 100}ms` }} />
                      {/* Title + meta */}
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="h-3.5 rounded animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200"
                               style={{ width: `${50 + i * 12}%`, animationDelay: `${i * 100}ms` }} />
                          <div className="h-3 w-12 rounded bg-slate-100 animate-pulse"
                               style={{ animationDelay: `${i * 100 + 50}ms` }} />
                        </div>
                        <div className="h-2.5 w-1/3 bg-slate-100 rounded animate-pulse"
                             style={{ animationDelay: `${i * 100 + 100}ms` }} />
                      </div>
                      {/* Right column — progress bar */}
                      <div className="hidden sm:block w-28 space-y-1.5">
                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-indigo-200 to-purple-200 animate-pulse"
                               style={{ width: `${30 + i * 20}%`, animationDelay: `${i * 100}ms` }} />
                        </div>
                        <div className="h-2 w-12 ml-auto bg-slate-100 rounded animate-pulse"
                             style={{ animationDelay: `${i * 100 + 150}ms` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Projects List — gated to NOT render during init/sync windows above */}
          {currentView === 'projects' && sidebarMode !== 'terminology' && sidebarMode !== ('shop' as any) && sidebarMode !== 'admin' && !isInitializing && !(isSyncingFromDisk && projects.length === 0) && (
            <ProjectsList
              projects={projects.filter(p => {
                if (sidebarMode === 'translating') return p.type === 'translate';
                if (sidebarMode === 'training') return p.type === 'train';
                return true;
              })}
              mode={sidebarMode === 'training' ? 'train' : 'translate'}
              files={files}
              onSelectProject={handleSelectProject}
              onImportProject={handleImportProject}
              onOpenFolder={handleOpenFolder}
              onNavigateToLab={() => {}}
              onCreateProject={(forcedType) => {
                if (forcedType) {
                  setCreateModalMode(forcedType as any);
                } else {
                  setCreateModalMode(sidebarMode === 'translating' ? 'translate' : 'train');
                }
              }}
              onConnectFS={handleConnectFS}
              onDeleteProject={handleDeleteProject}
              fsStatus={{
                isConnected: isFSConnectedEffective,
                folderName: fsFolderNameEffective,
                error: fsError || undefined,
              }}
            />
          )}

          {/* Terminology */}
          {sidebarMode === 'terminology' && (
            <TerminologyGlobal />
          )}

          {/* Project Detail */}
          {currentView === 'project-detail' && currentProject && (
            <ProjectDetail
              project={currentProject}
              files={files.filter(f => f.projectId === currentProject.id)}
              onBack={handleBackToProjects}
              onSelectFile={(id) => {
                selectFile(id);
                const file = files.find(f => f.id === id);
                // Chỉ auto vào Export khi đã dịch XONG, không phải đang running
                if (file?.aiOutput && file.aiOutput.length > 0 && file.currentStep !== 4
                    && file.translationStatus !== 'running' && file.translationStatus !== 'pending') {
                  const { updateFile: storeUpdate } = useProjectStore.getState();
                  storeUpdate(id, { currentStep: 4 });
                }
                handleSelectFile(id);
              }}
              onUpdateProject={handleUpdateProject}
              onAddFiles={handleAddFiles}
              onBatchImport={handleBatchImport}
              onDeleteFile={handleDeleteFile}
              onRunFile={(fileId) => {
                const _file = files.find(f => f.id === fileId);
                const _proj = projects.find(p => p.id === _file?.projectId);
                handleRunFileWithCreditCheck(fileId, _proj?.type === 'train' ? 'train' : 'translate');
              }}
              onRunTranslate={(fileId) => handleRunFileWithCreditCheck(fileId)}
              onRunQC={(fileId) => handleRunFileWithCreditCheck(fileId, 'qc')}
              onRunAll={() => {
                const pending = files.filter(f => f.projectId === currentProject?.id && (f.translationStatus === 'pending' || f.translationStatus === 'error'));
                if (pending.length === 0) {
                  showToast('Tất cả tài liệu đã được dịch rồi!', 'info');
                  return;
                }
                setShowRunAll(true);
              }}
              onExportProject={handleExportProject}
              initialTab={projectDetailTab}
              returnToFileId={returnToFileId}
              onReturnToFile={(fileId) => {
                setReturnToFileId(null);
                setProjectDetailTab('glossary');
                selectFile(fileId);
              }}
              streamingCount={streamingCountForQueue}
              totalSegments={activeTotalSegments}
              activeTranslatingFileId={activeTranslatingFileId || undefined}
            />
          )}

          {/* US-19-16: file detail loading guard. Block heavy-field render until contentLoaded.
              ensureFileContentLoaded fires from handleSelectFile; this gate avoids crash on
              undefined sourceText before async load completes. */}
          {currentView === 'file-detail' && currentFile && currentFile.contentLoaded === false && (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span className="text-sm">Đang tải nội dung tài liệu...</span>
              </div>
            </div>
          )}

          {/* File Detail — TRAIN mode: 5-step stepper (ported from App.tsx) */}
          {currentView === 'file-detail' && currentFile && currentFile.contentLoaded !== false && currentProject && currentProject.type === 'train' && (
            <div className="h-full flex flex-col">
              {/* File Header with Step Navigator */}
              <div className="px-6 py-3 border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleBackToProject}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      &larr; Quay lại
                    </button>
                    <span className="text-slate-300">|</span>
                    <span className="font-semibold text-slate-700">{currentFile.fileName}</span>
                  </div>

                  {/* Step Navigator */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const allSteps = [
                        { step: 1, label: 'Nguồn', done: true, running: false },
                        { step: 2, label: 'Mẫu đối chiếu', done: !!currentFile.humanBenchmark || !!currentProject.humanBenchmark, running: false },
                        { step: 2.5, label: 'Ghép cặp', done: !!currentFile.semanticMatchResult, running: currentFile.matchStatus === 'running' || isMatching },
                        { step: 3, label: 'Xem lại', done: currentFile.translationStatus === 'done', running: currentFile.translationStatus === 'running' || isTranslating },
                        { step: 4, label: 'Báo cáo', done: currentFile.auditStatus === 'done' && !!currentFile.auditResult, running: currentFile.auditStatus === 'running' || isAuditing }
                      ];

                      return allSteps.map((item, idx) => {
                        const isCurrent = currentFile.currentStep === item.step;
                        const canNavigate = item.step === 1 ||
                          (item.step === 2) ||
                          (item.step === 2.5 && (!!currentFile.humanBenchmark || !!currentProject.humanBenchmark)) ||
                          (item.step === 3 && currentFile.translationStatus === 'done') ||
                          (item.step === 4 && ((currentFile.auditStatus === 'done' && !!currentFile.auditResult) || currentFile.auditStatus === 'running' || isAuditing));

                        return (
                          <React.Fragment key={item.step}>
                            {idx > 0 && (
                              <div className={`w-6 h-0.5 ${item.running ? 'bg-amber-400' : item.done || isCurrent ? 'bg-blue-400' : 'bg-slate-200'}`} />
                            )}
                            <button
                              onClick={() => canNavigate && handleUpdateFile(currentFile.id, { currentStep: item.step as 1 | 2 | 3 | 4 })}
                              disabled={!canNavigate}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                item.running
                                  ? 'bg-amber-100 text-amber-700'
                                  : isCurrent
                                    ? 'bg-blue-500 text-white shadow-sm'
                                    : item.done
                                      ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                      : canNavigate
                                        ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                              }`}
                              title={item.label}
                            >
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                                item.running
                                  ? 'bg-amber-500 text-white'
                                  : isCurrent
                                    ? 'bg-white/20'
                                    : item.done
                                      ? 'bg-emerald-500 text-white'
                                      : 'bg-slate-200'
                              }`}>
                                {item.running ? (
                                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                ) : item.done && !isCurrent ? (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  item.step
                                )}
                              </span>
                              <span className="hidden sm:inline">{item.label}</span>
                            </button>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="flex-1 overflow-hidden p-4">
                {currentFile.currentStep === 1 && (
                  <SourceInput
                    sourceText={currentFile.sourceText}
                    setSourceText={(text) => handleUpdateFile(currentFile.id, { sourceText: text })}
                    rawSegments={getDisplaySegments(currentFile)}
                    hoveredSegmentIndex={hoveredSegmentIndex}
                    setHoveredSegmentIndex={setHoveredSegmentIndex}
                    autoSegmentSource={() => {
                      const segments = getDisplaySegments(currentFile);
                      handleUpdateFile(currentFile.id, { sourceText: segments.map(s => s.text).join('\n') });
                    }}
                    onContinue={() => handleUpdateFile(currentFile.id, { currentStep: 2 })}
                    onStartTranslation={() => handleRunFileWithCreditCheck(currentFile.id, 'train')}
                    isTranslating={isTranslating}
                    terminologyRules={currentProject.terminologyRules}
                    synonymGroups={currentProject.synonymGroups || []}
                  />
                )}

                {currentFile.currentStep === 2 && (
                  <BenchmarkInput
                    humanTranslation={currentFile.humanBenchmark || currentProject.humanBenchmark || ''}
                    setHumanTranslation={(text) => handleUpdateFile(currentFile.id, { humanBenchmark: text })}
                    onBack={() => handleUpdateFile(currentFile.id, { currentStep: 1 })}
                    onStartTranslation={() => {
                      handleRunFileWithCreditCheck(currentFile.id, 'train');
                      handleUpdateFile(currentFile.id, { currentStep: 2.5 });
                    }}
                    isTranslating={isTranslating}
                  />
                )}

                {currentFile.currentStep === 2.5 && (
                  <SemanticMatch
                    sourceSegments={getTrainSourceSegments(currentFile)}
                    humanSegments={getSegments(currentFile.humanBenchmark || currentProject.humanBenchmark || '').map(s => s.text)}
                    semanticMatchResult={currentFile.semanticMatchResult}
                    isMatching={isMatching || currentFile.matchStatus === 'running'}
                    onRunMatch={() => runSemanticMatchStep(currentFile.id)}
                    onContinue={() => handleUpdateFile(currentFile.id, { currentStep: 3 })}
                    onBack={() => handleUpdateFile(currentFile.id, { currentStep: 2 })}
                  />
                )}

                {/* Step 3 — TranslationReview */}
                {currentFile.currentStep === 3 && (
                  <TranslationReview
                    sourceSegments={getTrainSourceSegments(currentFile)}
                    aiOutputSegments={currentFile.aiOutput || []}
                    humanSegments={getSegments(currentFile.humanBenchmark || currentProject.humanBenchmark || '').map(s => s.text)}
                    semanticMatchResult={currentFile.semanticMatchResult ?? null}
                    matchedRules={currentFile.matchedRules}
                    isTranslating={isTranslating}
                    isAuditing={isAuditing || currentFile.auditStatus === 'running'}
                    isMatching={isMatching || currentFile.matchStatus === 'running'}
                    hasTranslationError={currentFile.translationStatus === 'error'}
                    hasMatchError={currentFile.matchStatus === 'error'}
                    realtimeProgress={realtimeProgress ?? undefined}
                    realtimeBatches={realtimeBatches}
                    onStartAudit={() => handleRunFileWithCreditCheck(currentFile.id, 'audit')}
                    onRunMatch={() => runSemanticMatchStep(currentFile.id)}
                    onRetranslate={() => handleRunFileWithCreditCheck(currentFile.id, 'retranslate')}
                    onPreviewPrompt={() => {}}
                    retranslateCredits={currentFile.estimatedCredits?.translate ?? null}
                  />
                )}

                {/* Step 4 — AuditReport (with result) */}
                {currentFile.currentStep === 4 && currentFile.auditResult && (
                  <AuditReport
                    auditResult={currentFile.auditResult}
                    sourceSegments={getTrainSourceSegments(currentFile)}
                    aiOutputSegments={currentFile.aiOutput || []}
                    humanSegments={getSegments(currentFile.humanBenchmark || currentProject.humanBenchmark || '').map(s => s.text)}
                    fileName={currentFile.fileName}
                    matchedRules={currentFile.matchedRules}
                    semanticMatchResult={currentFile.semanticMatchResult}
                    onBackToReview={() => handleUpdateFile(currentFile.id, { currentStep: 3 })}
                    onNewSession={handleBackToProject}
                    onShowDebug={() => {}}
                    onReaudit={() => handleRunFileWithCreditCheck(currentFile.id, 'audit')}
                    isAuditing={isAuditing || currentFile.auditStatus === 'running'}
                    reauditCredits={currentFile.estimatedCredits?.audit ?? null}
                  />
                )}

                {/* Step 4 — Fallback: audit running or missing result */}
                {currentFile.currentStep === 4 && !currentFile.auditResult && (
                  <div className="flex flex-col h-full p-4">
                    {currentFile.auditStatus === 'running' ? (
                      <div className="flex flex-col gap-4 h-full animate-in fade-in">
                        <div className="flex items-center gap-3 shrink-0">
                          <svg className="w-5 h-5 animate-spin text-amber-500" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <h3 className="text-lg font-bold text-slate-800">Audit đang chạy...</h3>
                          <span className="text-xs text-slate-400 font-medium">
                            {!auditStreamingText ? 'AI đang phân tích...' : 'Đang tạo kết quả...'}
                          </span>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto" ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}>
                          {(() => {
                            if (!auditStreamingText) return (
                              <div className="flex items-center justify-center h-40 text-slate-400 text-sm">
                                <svg className="w-4 h-4 mr-2 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                Đang chờ AI phản hồi...
                              </div>
                            );
                            const lines = auditStreamingText.split('\n').filter(l => l.trim());
                            const headerLine = lines[0] || '';
                            const isCSV = headerLine.includes('|');
                            if (!isCSV) {
                              return (
                                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap font-mono">
                                  {auditStreamingText}
                                  <span className="animate-pulse text-blue-400">|</span>
                                </div>
                              );
                            }
                            const headers = headerLine.split('|').map(h => h.trim());
                            const dataRows = lines.slice(1).filter(l => l.includes('|'));
                            const scoreIdx = headers.findIndex(h => h.toLowerCase() === 'score');
                            const completedCount = dataRows.length;
                            return (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                                  <span className="font-semibold text-slate-700">{completedCount}</span> đoạn đã phân tích
                                  <span className="animate-pulse text-blue-400 ml-1">...</span>
                                </div>
                                {dataRows.map((row, rowIdx) => {
                                  const cells = row.split('|').map(c => c.trim());
                                  const score = scoreIdx >= 0 ? parseFloat(cells[scoreIdx]) : NaN;
                                  const scoreColor = isNaN(score) ? 'bg-slate-100 text-slate-600'
                                    : score >= 8 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : score >= 5 ? 'bg-amber-50 text-amber-700 border-amber-200'
                                    : 'bg-red-50 text-red-700 border-red-200';
                                  const details = headers.map((h, i) => ({ key: h, value: cells[i] || '' })).filter(d => d.value && d.key.toLowerCase() !== 'score');
                                  return (
                                    <div key={rowIdx} className={`border rounded-lg p-3 text-xs ${scoreColor} animate-in fade-in slide-in-from-bottom-2`} style={{ animationDelay: `${rowIdx * 30}ms` }}>
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-bold text-[10px] uppercase tracking-wider opacity-60">#{rowIdx + 1}</span>
                                        {!isNaN(score) && <span className="font-bold text-sm">{score}/10</span>}
                                      </div>
                                      <div className="space-y-0.5 mt-1">
                                        {details.slice(0, 4).map((d, di) => (
                                          <div key={di} className="flex gap-1.5">
                                            <span className="font-semibold opacity-60 shrink-0 min-w-[70px]">{d.key}:</span>
                                            <span className="break-all">{d.value}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                                <div className="h-4 flex items-center">
                                  <span className="animate-pulse text-blue-400 text-lg">|</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <svg className="w-16 h-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-slate-500 text-sm font-medium">
                          {currentFile.auditStatus === 'done'
                            ? 'Kết quả audit bị mất. Vui lòng chạy lại.'
                            : 'Chưa có kết quả audit.'}
                        </p>
                        <button
                          onClick={() => runAudit(currentFile.id)}
                          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-bold hover:bg-amber-600 transition-colors"
                        >
                          Chạy lại Audit
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* File Detail — TRANSLATE mode: export wrapper */}
          {currentView === 'file-detail' && currentFile && currentFile.contentLoaded !== false && currentProject && currentProject.type !== 'train' && (
            <div className="h-full flex flex-col">
              {/* File header */}
              <div className="px-6 py-3 border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleBackToProject}
                      className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                    </button>
                    <div>
                      <h2 className="font-bold text-sm text-slate-800">{currentFile.fileName}</h2>
                      <p className="text-xs text-slate-400">{currentProject.name}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* File content - Translation Export (reuse) */}
              <div className="flex-1 overflow-y-auto">
                <TranslationExportWrapper
                    file={currentFile}
                    onBackToSource={() => handleUpdateFile(currentFile.id, { currentStep: 1 })}
                    onNewSession={handleBackToProject}
                    onRetranslate={() => handleRunFileWithCreditCheck(currentFile.id, 'retranslate')}
                    isRetranslating={isTranslating && activeTranslatingFileId === currentFile.id}
                    onRunQualityCheck={handleRunQualityCheck}
                    isQualityChecking={isQualityChecking}
                    qcOutputLang={qcOutputLang || 'vi'}
                    onQcOutputLangChange={setQcOutputLang}
                    projectSourceLang={currentProject.sourceLang || 'vi'}
                    onRetryBatch={(batchIndex) => {
                      retryBatches(currentFile.id, [batchIndex]).catch((e: any) => {
                        showToast(`Retry thất bại: ${e?.message || 'Lỗi không xác định'}`, 'error');
                      });
                    }}
                    onRetryAllFailed={() => {
                      const failed = (currentFile.batchInfo || [])
                        .filter(b => b.status !== 'done')
                        .map(b => b.index);
                      if (failed.length > 0) {
                        retryBatches(currentFile.id, failed).catch((e: any) => {
                          showToast(`Retry thất bại: ${e?.message || 'Lỗi không xác định'}`, 'error');
                        });
                      }
                    }}
                    onUpdateAiOutput={(newAiOutput) => handleUpdateFile(currentFile.id, { aiOutput: newAiOutput })}
                    onUpdateQualityResult={(newResult) => handleUpdateFile(currentFile.id, { auditResult: newResult })}
                />
              </div>
            </div>
          )}

        </main>
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={createModalMode !== null}
        mode={createModalMode || 'select'}
        onClose={() => setCreateModalMode(null)}
        onCreate={(name, type) => {
          setCreateModalMode(null);
          handleCreateProject(name, type as ProjectType);
        }}
      />

      {/* Celebration Modal */}
      <CelebrationModal
        isOpen={showCelebration}
        title={celebrationData.title}
        subtitle={celebrationData.subtitle}
        value={celebrationData.value}
        onClose={() => setShowCelebration(false)}
      />

      {/* Data Shredder (privacy animation) */}
      <DataShredder
        isOpen={showShredder}
        onClose={() => setShowShredder(false)}
      />

      {/* Bank Transfer Payment Modal */}
      <BankTransferModal
        isOpen={showBankTransfer}
        purchaseData={purchaseData}
        accessToken={session?.access_token}
        onClose={() => { setShowBankTransfer(false); setPurchaseData(null); }}
        onConfirmPaid={() => {
          showToast('Da ghi nhan! He thong se xac nhan trong 2-5 phut.', 'success');
        }}
        onConfirmed={() => {
          showToast(`Thanh toan thanh cong! +${purchaseData?.credits_total.toLocaleString()} credits`, 'success');
          refetchCredits();
        }}
      />

      {/* Run All Modal — multi-file selector */}
      {showRunAll && (() => {
        const pending = files.filter(f => f.projectId === currentProject?.id && (f.translationStatus === 'pending' || f.translationStatus === 'error'));
        return (
          <RunAllModal
            isOpen={showRunAll}
            onClose={() => setShowRunAll(false)}
            pendingFiles={pending}
            creditsAvailable={(profile?.role === 'admin' || profile?.role === 'internal') ? Infinity : (balance?.credits ?? 0)}
            onBuyCredits={() => { setShowRunAll(false); navigate('/app/shop'); }}
            onConfirm={async (selectedIds, qcIds) => {
              setShowRunAll(false);
              if (selectedIds.length === 0) return;
              // Set skipQC per-file theo qcIds (file in qcIds → skipQC=false, file out → skipQC=true)
              const store = useProjectStore.getState();
              const qcSet = new Set(qcIds);
              for (const id of selectedIds) {
                store.updateFile(id, { skipQC: !qcSet.has(id) });
              }
              // Parallel run — workflow tự handle credit check + sourceText hydrate.
              // Worker TPM gate (US-19-20) throttle batches server-side, không cần gate client-side.
              // Promise.allSettled = fail-isolated, 1 file lỗi không stop file khác.
              const results = await Promise.allSettled(
                selectedIds.map((id: string) => runTranslateJob(id))
              );
              const failed = results
                .map((r, i) => r.status === 'rejected' ? { id: selectedIds[i], err: r.reason } : null)
                .filter(Boolean);
              if (failed.length > 0) {
                console.error('[RunAll] failed files:', failed);
                showToast(`${failed.length}/${selectedIds.length} file lỗi — xem console`, 'error');
              }
            }}
          />
        );
      })()}

      {/* Credit Confirm Modal (trước khi dịch) */}
      {showCreditConfirm && creditCheckResult && (() => {
        const pendingFile = pendingTranslateFileId
          ? files.find(f => f.id === pendingTranslateFileId)
          : null;
        const showQC = pendingRunMode === 'translate' && !!pendingFile;
        const includeQC = !!pendingFile && !pendingFile.skipQC;
        return (
          <CreditConfirmModal
            isOpen={showCreditConfirm}
            fileName={creditCheckResult.fileName || 'Tài liệu'}
            creditCheck={creditCheckResult}
            mode={pendingRunMode}
            onConfirm={handleConfirmTranslate}
            onCancel={() => { setShowCreditConfirm(false); setPendingTranslateFileId(null); }}
            onBuyCredits={() => { setShowCreditConfirm(false); setPendingTranslateFileId(null); setSidebarMode('shop' as any); }}
            showQCToggle={showQC}
            includeQC={includeQC}
            onToggleQC={() => {
              if (pendingFile) {
                useProjectStore.getState().updateFile(pendingFile.id, { skipQC: includeQC });
              }
            }}
            qcCredits={pendingFile?.estimatedCredits?.qc ?? null}
          />
        );
      })()}

      {/* US-04-13 Layer 2: Bug Report Modal — opt-in file upload after translate fail */}
      <BugReportModal
        isOpen={!!bugReportContext}
        onClose={clearBugReportContext}
        errorMessage={bugReportContext?.errorMessage}
        fileName={bugReportContext?.fileName}
        jobId={bugReportContext?.jobId}
      />

      {/* Phone Verify Modal (Deferred OTP) */}
      <PhoneVerify
        isOpen={showPhoneVerify}
        onClose={() => { setShowPhoneVerify(false); setOtpDismissed(true); sessionStorage.setItem('otp_dismissed', 'true'); }}
        onVerified={() => {
          verifyPhone();
          setShowPhoneVerify(false);
          setCelebrationData({ title: 'Chúc mừng!', subtitle: 'Dịch được khoảng 60 trang tài liệu dày', value: '100,000 credits' });
          setShowCelebration(true);
        }}
      />

      {/* US-07-04 Onboarding Tour — gated behind folder connection (US-07-01) */}
      <OnboardingTour
        userName={user?.email}
        enabled={isFSConnectedEffective}
      />

      {/* US-07-08 Stage 4 Tour — project workflow deep-dive, trigger khi mở project đầu tiên */}
      <Stage4ProjectTour
        isInProjectDetail={currentView === 'project-detail' && !!currentProject}
        projectIndustry={currentProject?.industry}
        onComplete={handleBackToProjects}
      />

    </div>
  );
};

export default CommercialApp;
